import type {
    ComposableOptionsBase,
    Product,
    ProductFilterAttribute,
    ProductFilterByType,
    ProductFilterTrackingObj,
    ProductsFilterParamsObj,
    ProductFilterModel,
    ProductFilterModelItem,
    ContentMetaResponse
} from '~/types';
import type {
    CustomPagination,
    PaginatedPayload
} from 'x1-base/types';
import { useApiUtils } from 'x1-base/composables';
import { useCustomFetch } from '~/composables';
import { omit as lo_omit } from 'es-toolkit';
import { trim as lo_trim } from 'es-toolkit';
import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
import { objFilterNulls, pathJoin } from 'x1-base/utils';
import { ProductsQueryParamsModel } from '~/models';
import type { NitroFetchOptions } from 'nitropack';
import type { LocationQuery } from 'vue-router';

const defaultPageSize = 12;
const defaultFiltersPageSize = 1000;

export interface useProductCollectionOptions extends ComposableOptionsBase {}
export function useProductCollection(options: Partial<UseProductsOptions> = {}) {
    const useApiUtilsObj = useApiUtils();
    const apiBasePathFilters = `/v1/attributes`;
    const apiBasePathProducts = `/v1/ct/`;
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });

    const apiResourcePath = useState<string>('keyProdCollResPath', () => '');
    const preSelectedFilters = useState<string[]>('keyProdCollPerSelFilters', () => []);
    const queryParamsObj = useState<ProductsFilterParamsObj>('keyProdCollFilterParamsObj', () => getProductsQueryParamsModelObj());
    const filtersModel = useState<Nullable<ProductFilterModel>>('keyProdCollFiltersModel', () => null);
    const filtersAppliedCount = useState<number>('keyProdCollAplFltrCt', () => 0);
    const hasNextLink = useState<boolean>('keyProdCollHasNextLink', () => false);
    const hasPrevLink = useState<boolean>('keyProdCollHasPrevLink', () => false);
    const nextQueryParams = useState<ProductsFilterParamsObj>('keyProdCollNextQueryParams', () => ({}));
    const prevQueryParams = useState<ProductsFilterParamsObj>('keyProdCollPrevQueryParams', () => ({}));
    const totalCount = useState<number>('keyProdCollTotalCount', () => 0);


    function init(resourceUid: string, resourcePath: string, queryParams: LocationQuery = {}) {
        apiResourcePath.value = resourcePath;
        const defaultQs = createDefaultQueryParams(resourceUid);
        Object.assign(queryParamsObj.value, defaultQs, queryParams);
    }

    async function getCollection(queryObj: ProductsFilterParamsObj = {}, noPageSize = false): Promise<PaginatedPayload<Product> | Error> {
        let url = pathJoin(apiBasePathProducts, apiResourcePath.value);
        url = `${lo_trim(url, '/')}/`;

        const query = getQueryObj({
            page_size: defaultPageSize,
            ...queryObj
        });

        if (noPageSize) {
            delete query.page_size;
        }

        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query
        };

        try {
            const response = await $_fetch<ContentMetaResponse>(url, config);
            const contentResponse: CustomPagination<Product> = response.content as CustomPagination<Product>;
            setState(contentResponse);

            const paginatedPayload = {
                pagination: lo_omit(contentResponse, ['results']),
                data: contentResponse.results
            };

            return paginatedPayload;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getFilters(): Promise<ProductFilterAttribute[] | Error> {
        const url = `${apiBasePathFilters}/`;

        const params = getQueryObj({
            page_size: defaultFiltersPageSize,
            expand: ['values'],
            is_filter: true
        });

        try {
            const response = await $_fetch<CustomPagination<ProductFilterAttribute>>(url, { params });

            setFiltersModel(response.results);

            return response.results;
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    // Pagination Sugar
    async function getNext(): Promise<PaginatedPayload<Product> | Error> {
        return await getCollection(nextQueryParams.value, true);
    }

    async function getPrev(): Promise<PaginatedPayload<Product> | Error> {
        return await getCollection(prevQueryParams.value, true);
    }

    // Helpers

    function setFiltersModel(data: ProductFilterAttribute[]): void {
        const model: ProductFilterModel = {};
        const preSelected = queryParamsObj.value?.f ?? [];

        data.map((obj: ProductFilterAttribute) => {
            const valsModel: ProductFilterModelItem = {};

            obj.values.forEach((value) => {
                const isPreSelected = preSelected.includes(value.uid!);

                if (isPreSelected) {
                    filtersAppliedCount.value++;
                }

                valsModel[value.uid!] = isPreSelected;
            });

            model[obj.uid!] = valsModel;
        });

        filtersModel.value = model;
    }

    function processFiltersModel(): string[] {
        const selectedItems: string[] = [];
        let count = 0;

        if (filtersModel.value) {
            Object.values(filtersModel.value).forEach((model) => {
                for (const key in model) {
                    if (!!model[key]) {
                        selectedItems.push(key);
                        count++;
                    }
                }
            });
        }

        filtersAppliedCount.value = count;
        return selectedItems;
    }

    function resetFiltersModel(): void {
        for (const key in filtersModel.value) {
            for (const k in filtersModel.value[key]) {
                filtersModel.value[key][k] = false;
            }
        }
    }

    function getQueryObj(extendObj: Partial<ProductsFilterParamsObj> = {}): ProductsFilterParamsObj & LocationQuery {
        const query = {
            ...objFilterNulls(queryParamsObj.value),
            ...extendObj
        };

        return query;
    }

    function createDefaultQueryParams(defaultUid: string): ProductsFilterParamsObj {
        const params = getProductsQueryParamsModelObj();

        if (defaultUid) {
            const type = getUidType(defaultUid);

            if (type) {
                params[type] = defaultUid;
            }
        }

        return params;
    }

    function getUidType(uid: string): ProductFilterByType {
        const uid_prefix = String(uid).split('_')[0];
        switch (uid_prefix) {
            case 'cat':
                return 'category';
            case 'mkt':
                return 'market';
            case 'col':
                return 'collection';
            default:
                return 'category';
        }
    }

    function processContentResponse(response: CustomPagination<Product>): PaginatedPayload<Product> {
        setState(response);
        return useApiUtilsObj.customToPaginatedPayload(response);
    }

    function setState(response: CustomPagination<Product>): void {
        const { next, previous, count } = response;
        hasNextLink.value = !lo_isEmpty(next);
        hasPrevLink.value = !lo_isEmpty(previous);
        nextQueryParams.value = useApiUtilsObj.getPaginationQueryParams(next);
        prevQueryParams.value = useApiUtilsObj.getPaginationQueryParams(previous);
        totalCount.value = count ?? 0;
    }

    function resetState(): void {
        apiResourcePath.value = '';
        preSelectedFilters.value = [];
        queryParamsObj.value = getProductsQueryParamsModelObj();
        totalCount.value = 0;
        resetFilters();
    }

    function resetFilters(): void {
        hasNextLink.value = false;
        hasPrevLink.value = false;
        nextQueryParams.value = {};
        prevQueryParams.value = {};
        filtersAppliedCount.value = 0;

        if (queryParamsObj.value) {
            const keys = [ 'f', 'filter', 'search' ] as const;

            keys.forEach((k) => {
                queryParamsObj.value[k] = null;
            });
        }

        resetFiltersModel();
    }

    function getProductsQueryParamsModelObj(): ProductsFilterParamsObj {
        return (new ProductsQueryParamsModel()).toPlainObject();
    }

    return {
        init,
        hasNextLink,
        hasPrevLink,
        totalCount,
        getCollection,
        getNext,
        getPrev,
        getFilters,
        getQueryObj,
        filtersModel,
        filtersAppliedCount,
        processFiltersModel,
        resetFiltersModel,
        preSelectedFilters,
        queryParamsObj,
        createDefaultQueryParams,
        setState,
        resetState,
        resetFilters,
        processContentResponse
    };
}
